'use client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { parseUnits } from 'viem';

import { usePortfolioSummary } from '@/services/query';
import { useCollateralOnramp } from './useCollateralOnramp';

import { MESSAGES } from '@/constants/messages.constants';

/**
 * useAutoCollateralMigration
 *
 * A background utility hook that monitors for legacy USDC.e balances and automatically
 * prompts or executes a migration to pUSD.
 *
 * context: Polymarket V2 requires pUSD for all CLOB trading. Users with legacy USDC.e
 * need to "wrap" their funds to pUSD to have usable trading firepower.
 */
export function useAutoCollateralMigration() {
  const { data: summary } = usePortfolioSummary();
  const { wrap, wrapNativeUsdc, isPending } = useCollateralOnramp();

  // Track if user has explicitly rejected/canceled a migration in the current session
  const [hasRejected, setHasRejected] = useState(false);

  // Ref to prevent overlapping migration attempts
  const isMigratingRef = useRef(false);

  const handleError = useCallback((error: any, label: string) => {
    console.error(`[AutoMigration] ${label} failed:`, error);
    const wasRejected =
      error.message?.toLowerCase().includes('user rejected') || error.code === 4001;
    if (wasRejected) {
      setHasRejected(true);
      // toast.warn(MESSAGES.TOAST.AUTO_MIGRATION_PAUSED);
    }
  }, []);

  /**
   * Converts USDC.e → pUSD (legacy collateral wrapping).
   */
  const handleLegacyMigration = useCallback(
    async (balance: number) => {
      if (isMigratingRef.current) return;
      isMigratingRef.current = true;
      try {
        const amount = parseUnits(balance.toFixed(6), 6);
        await wrap(amount);
        // toast.success(`Automatically migrated ${balance.toFixed(2)} USDC.e to pUSD`);
      } catch (error: any) {
        handleError(error, 'USDC.e migration');
      } finally {
        isMigratingRef.current = false;
      }
    },
    [wrap, handleError]
  );

  /**
   * Converts native USDC → pUSD.
   * Approves the CollateralOnramp and wraps in one batched Safe transaction.
   */
  const handleNativeUsdcMigration = useCallback(
    async (balance: number) => {
      if (isMigratingRef.current) return;
      isMigratingRef.current = true;
      try {
        const amount = parseUnits(balance.toFixed(6), 6);
        await wrapNativeUsdc(amount);
      } catch (error: any) {
        handleError(error, 'Native USDC migration');
      } finally {
        isMigratingRef.current = false;
      }
    },
    [wrapNativeUsdc, handleError]
  );

  // Monitor portfolio summary and auto-migrate any unwrapped USDC.e → pUSD.
  // NOTE: Native USDC (0x3c499...) is NOT auto-migrated — Polymarket's
  // CollateralOnramp contract only accepts USDC.e, not native USDC.
  // Native USDC must be manually swapped to USDC.e on a DEX (QuickSwap/Uniswap)
  // before it can be used for trading.
  useEffect(() => {
    const legacyBalance = summary?.legacyUsdcBalance || 0;

    if (legacyBalance > 0.01 && !hasRejected && !isPending && !isMigratingRef.current) {
      void handleLegacyMigration(legacyBalance);
    }
  }, [summary, handleLegacyMigration, isPending, hasRejected]);

  return {
    isAutoMigrating: isPending || isMigratingRef.current,
    hasRejected,
  };
}
